/**
 * Launchpad + Tabs — board "Launchpad": a Home of folder cards that drill down
 * in place (any depth), with Continue / Open editors / Pinned / Recent beside
 * them, and a tab strip of the open screens with the open editors as DRAFT
 * tabs in the same row. A screen opens in its own tab under a breadcrumb title.
 *
 * Presentational: the open screens, editors, pins and recents are the shared
 * model's. The only state kept here is view state — whether Home is showing
 * and which folder each Home card has drilled into.
 *
 * Home is a VIEW, not "no screen": going Home keeps every tab open (the
 * model's `close()` would close the current one). While Home shows, the
 * ShellFrame stays mounted and is only hidden, so the current screen keeps its
 * filters / scroll and the kept-alive open editors are never unmounted.
 */
import { Home as HomeIcon } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { tw } from '../../testHooks'
import { EmptyPane } from '../nav/parts/EmptyPane'
import { Breadcrumb, ScreenTitle } from '../nav/parts/ScreenTitle'
import { ShellFrame } from '../nav/ShellFrame'
import { S } from '../nav/tokens'
import type { RunNavItem } from '../nav/types'
import { LOOSE_CARD, LaunchpadHome } from './launchpad/Home'
import { TabStrip, tabId } from './launchpad/TabStrip'
import type { ShellLayoutProps } from './types'

/** How long the draft tabs are highlighted after "Open editors" is asked for. */
const DRAFT_PULSE_MS = 1600

export default function LaunchpadLayout({
  model, appTitle, themeColor, compact = false, renderObject, headerExtras,
  emptyNavMessage, emptyPaneMessage,
}: ShellLayoutProps) {
  const accent = themeColor || S.accent
  const idBase = useId()
  const homePanelId = `${idBase}-home`
  const screenPanelId = `${idBase}-screen`
  const stripRef = useRef<HTMLDivElement>(null)
  const editorsRef = useRef<HTMLElement>(null)

  const [showHome, setShowHome] = useState(false)
  const [drill, setDrillState] = useState<Record<string, string[]>>({})
  const [focusCard, setFocusCard] = useState<{ id: string; n: number } | null>(null)
  const [pulseDrafts, setPulseDrafts] = useState(false)

  const current = model.current
  const full = model.fullScreen
  // Whatever becomes current — a tile, a smart-page link, Back, a restored
  // editor — is what the user wants to see, so Home steps aside.
  const currentId = current?.id ?? null
  useEffect(() => { setShowHome(false) }, [currentId])
  useEffect(() => { if (model.activeEditorId) setShowHome(false) }, [model.activeEditorId])

  const homeShown = !full && (!current || showHome)
  const act = model.activeEditor
  const shownEditor = act && current && act.objName === current.obj_name ? act : null

  const goHome = () => {
    setShowHome(true)
    // An editor shown over the screen would otherwise float over Home too.
    if (model.activeEditorId) model.minimiseEditor()
    if (model.fullScreen) model.setFullScreen(false)
  }
  const openItem = (item: RunNavItem) => {
    setShowHome(false)
    if (current?.id !== item.id) model.open(item)
  }
  const onScreenTab = (item: RunNavItem) => {
    setShowHome(false)
    // The screen's own tab while its editor is up: back to the list (the
    // editor waits in its draft tab).
    if (current?.id === item.id) { if (shownEditor) model.minimiseEditor(); return }
    model.open(item)
  }
  const onDraftTab = (id: string) => {
    setShowHome(false)
    model.restoreEditor(id)
  }
  const setDrill = (cardId: string, ids: string[]) => setDrillState((d) => ({ ...d, [cardId]: ids }))

  // The screen title's breadcrumb: show Home with the right card drilled to
  // that folder (any depth — the card's trail is the folder's whole chain).
  const showFolder = (folder: RunNavItem) => {
    const chain = [...model.pathOf(folder), folder]
    const root = chain[0]
    const cardId = root.obj_type === '-' ? root.id : LOOSE_CARD
    const ids = (root.obj_type === '-' ? chain.slice(1) : chain).map((c) => c.id)
    setDrill(cardId, ids)
    setFocusCard((f) => ({ id: cardId, n: (f?.n || 0) + 1 }))
    goHome()
  }

  // "Open editors" (the cap notice): the draft tabs ARE the list — focus the
  // first and highlight them; with none, the Home section.
  useEffect(() => {
    if (!model.editorsListRequest) return undefined
    setPulseDrafts(true)
    const raf = requestAnimationFrame(() => {
      const first = stripRef.current?.querySelector<HTMLElement>('[data-draft-tab]')
      if (first) {
        first.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
        first.focus()
      } else {
        editorsRef.current?.focus()
      }
    })
    const t = setTimeout(() => setPulseDrafts(false), DRAFT_PULSE_MS)
    return () => { cancelAnimationFrame(raf); clearTimeout(t) }
  }, [model.editorsListRequest])

  const screenLabelledBy = shownEditor ? tabId(idBase, `e:${shownEditor.id}`)
    : current ? tabId(idBase, `s:${current.id}`) : undefined
  const path = current ? model.pathOf(current) : []

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', flex: 1, width: '100%', height: '100%',
      minHeight: 0, overflow: 'hidden', background: S.bg,
    }}>
      {!full && (
        <TabStrip
          model={model}
          idBase={idBase}
          homePanelId={homePanelId}
          screenPanelId={screenPanelId}
          homeShown={homeShown}
          shownEditorId={shownEditor?.id ?? null}
          accent={accent}
          pulseDrafts={pulseDrafts}
          onHome={goHome}
          onScreen={onScreenTab}
          onDraft={onDraftTab}
          stripRef={stripRef}
        />
      )}
      {homeShown && (
        <LaunchpadHome
          model={model}
          appTitle={appTitle}
          emptyNavMessage={emptyNavMessage}
          compact={compact}
          panelId={homePanelId}
          labelledBy={tabId(idBase, 'home')}
          drill={drill}
          setDrill={setDrill}
          focusCard={focusCard}
          openItem={openItem}
          onPicked={() => setShowHome(false)}
          editorsRef={editorsRef}
        />
      )}
      {/* Always mounted (hidden under Home) — see the header comment. The
          title and frame keep their positions in the tree in full screen too,
          so entering / leaving it never remounts the screen. */}
      <div
        id={screenPanelId}
        role={full ? undefined : 'tabpanel'}
        aria-labelledby={full ? undefined : screenLabelledBy}
        style={{
          display: homeShown ? 'none' : 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
          padding: full ? 0 : (compact ? '0 8px 8px' : '0 24px 20px'),
        }}
      >
        {!full && current && (
          <div style={{ flexShrink: 0, padding: compact ? '8px 4px 4px' : '14px 4px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 26, minWidth: 0 }}>
              <button type="button" className="tw-sh-btn" onClick={goHome}
                {...tw('action', 'shell-launchpad-crumb-home')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0, minHeight: 26,
                  padding: '2px 6px', border: 'none', borderRadius: 5, background: 'transparent',
                  color: S.accent, fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                }}>
                <HomeIcon size={14} aria-hidden />
                Home
              </button>
              {path.length > 0 && <span aria-hidden style={{ color: S.faint, fontSize: 13 }}>›</span>}
              <Breadcrumb path={path} onFolder={showFolder} />
            </div>
            <ScreenTitle
              item={current}
              bar={false}
              height={compact ? 42 : 48}
              compact={compact}
              pinned={model.isPinned(current)}
              onTogglePin={() => model.togglePin(current)}
              fullScreen={full}
              onToggleFullScreen={() => model.setFullScreen(!full)}
              onClose={() => model.closeScreen(current)}
              extras={headerExtras?.(current)}
              style={{ padding: '0 0 0 4px' }}
            />
          </div>
        )}
        <div style={{
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: full ? S.bg : S.panel,
          border: full ? 'none' : `1px solid ${S.border}`,
          borderRadius: full ? 0 : 14,
        }}>
          <ShellFrame
            model={model}
            renderObject={renderObject}
            padding={compact ? '0.5rem' : '0.5rem 1rem 1rem'}
            empty={<EmptyPane model={model} appTitle={appTitle} message={emptyPaneMessage} accent={themeColor} />}
          />
        </div>
      </div>
    </div>
  )
}
